import { inject } from '@angular/core';
import { map, Observable } from 'rxjs';
import { UsersListService } from '@admin-panel/infra';
import { DateFormats, formatDateToString } from '@core';
import {
  DailyStatsDto,
  GetAdminUsersStatsResponse,
  PlatformStatsDto,
  PlatformVersionStatsDto,
  RegionDailyRegistrationsDto,
  RegionUserStatsDto,
  VersionStatsDto
} from '@core-http';
import {
  AdminUsersSummaryQueryResult,
  DailyRegionRegistrationQueryResult,
  DailyRegistrationQueryResult,
  PlatformStatQueryResult,
  RegionDailyStatQueryResult,
  RegionRegistrationsGroupQueryResult,
  VersionStatQueryResult
} from './admin-users-summary-data.query-result';

export class AdminUsersSummaryDataQueryHandler {
  private readonly usersListService: UsersListService = inject(UsersListService);

  query(registrationStatsDays?: number): Observable<AdminUsersSummaryQueryResult> {
    return this.usersListService
      .getUsersStats({ registrationStatsDays })
      .pipe(map((response: GetAdminUsersStatsResponse) => this.mapToResult(response)));
  }

  private mapToResult(response: GetAdminUsersStatsResponse): AdminUsersSummaryQueryResult {
    const platformVersionStats: PlatformVersionStatsDto[] = response.platformVersionStats;

    return {
      totalUsers: response.totalUsers,
      platformStats: response.platformStats
        .filter((stat: PlatformStatsDto) => ['ios', 'android'].includes(stat.platform.toLowerCase()))
        .map(
          (stat: PlatformStatsDto): PlatformStatQueryResult => ({
            platform: this.getPlatformName(stat.platform),
            count: stat.count,
            icon: this.getPlatformIcon(stat.platform)
          })
        )
        .sort((a: PlatformStatQueryResult, b: PlatformStatQueryResult) => b.count - a.count),
      versionStats: this.sortVersionStats(
        response.versionStats.map((stat: VersionStatsDto): VersionStatQueryResult => {
          const iosCount: number =
            platformVersionStats.find(
              (pv: PlatformVersionStatsDto) => pv.version === stat.version && pv.platform.toLowerCase() === 'ios'
            )?.count ?? 0;
          const androidCount: number =
            platformVersionStats.find(
              (pv: PlatformVersionStatsDto) => pv.version === stat.version && pv.platform.toLowerCase() === 'android'
            )?.count ?? 0;

          return {
            version: stat.version,
            count: stat.count,
            iosCount,
            androidCount
          };
        })
      ),
      dailyRegistrations: this.mapDailyRegistrations(
        response.dailyRegistrations ?? [],
        response.regionDailyRegistrations ?? []
      ),
      registrationsByRegion: this.mapRegistrationsByRegion(response.regionDailyRegistrations ?? []),
      regionStats: [...(response.regionStats ?? [])]
        .filter((stat: RegionUserStatsDto) => stat.userCount > 0)
        .sort((a: RegionUserStatsDto, b: RegionUserStatsDto) => b.userCount - a.userCount)
        .map((stat: RegionUserStatsDto) => ({ regionName: stat.regionName, userCount: stat.userCount }))
    };
  }

  private mapDailyRegistrations(
    stats: DailyStatsDto[],
    regionDailyRegistrations: RegionDailyRegistrationsDto[]
  ): DailyRegistrationQueryResult[] {
    const regionsByDate: Map<string, DailyRegionRegistrationQueryResult[]> = new Map();

    for (const region of regionDailyRegistrations) {
      for (const daily of region.dailyRegistrations) {
        const existing: DailyRegionRegistrationQueryResult[] = regionsByDate.get(daily.date) ?? [];
        if (daily.count > 0) {
          existing.push({ regionName: region.regionName, count: daily.count });
        }
        regionsByDate.set(daily.date, existing);
      }
    }

    return [...stats]
      .sort((a: DailyStatsDto, b: DailyStatsDto) => b.date.localeCompare(a.date))
      .map(
        (dto: DailyStatsDto): DailyRegistrationQueryResult => ({
          date: formatDateToString(dto.date, DateFormats.DayMonthYearDash),
          rawDate: dto.date,
          dayOfWeek: formatDateToString(dto.date, DateFormats.WeekdayName),
          count: dto.count,
          regions: (regionsByDate.get(dto.date) ?? []).sort(
            (a: DailyRegionRegistrationQueryResult, b: DailyRegionRegistrationQueryResult) => b.count - a.count
          )
        })
      );
  }

  private mapRegistrationsByRegion(
    regionDailyRegistrations: RegionDailyRegistrationsDto[]
  ): RegionRegistrationsGroupQueryResult[] {
    return [...regionDailyRegistrations]
      .map((region: RegionDailyRegistrationsDto): RegionRegistrationsGroupQueryResult => {
        const dailyRegistrations: RegionDailyStatQueryResult[] = [...region.dailyRegistrations]
          .filter((daily: DailyStatsDto) => daily.count > 0)
          .sort((a: DailyStatsDto, b: DailyStatsDto) => b.date.localeCompare(a.date))
          .map((daily: DailyStatsDto) => ({
            date: formatDateToString(daily.date, DateFormats.DayMonthYearDash),
            rawDate: daily.date,
            dayOfWeek: formatDateToString(daily.date, DateFormats.WeekdayName),
            count: daily.count
          }));

        const total: number = dailyRegistrations.reduce(
          (sum: number, d: RegionDailyStatQueryResult) => sum + d.count,
          0
        );

        return { regionName: region.regionName, total, dailyRegistrations };
      })
      .filter((region: RegionRegistrationsGroupQueryResult) => region.total > 0)
      .sort((a: RegionRegistrationsGroupQueryResult, b: RegionRegistrationsGroupQueryResult) => b.total - a.total);
  }

  private getPlatformName(platform: string): string {
    const platformLower: string = platform.toLowerCase();
    if (platformLower === 'ios') return 'iOS';
    if (platformLower === 'android') return 'Android';
    return platform;
  }

  private getPlatformIcon(platform: string): string {
    const platformLower: string = platform.toLowerCase();
    if (platformLower === 'ios') return 'assets/icons/apple.svg';
    if (platformLower === 'android') return 'assets/icons/android.svg';
    return '';
  }

  private sortVersionStats(stats: VersionStatQueryResult[]): VersionStatQueryResult[] {
    return [...stats].sort((a: VersionStatQueryResult, b: VersionStatQueryResult) => {
      if (a.version === 'null' || !a.version) return 1;
      if (b.version === 'null' || !b.version) return -1;

      const partsA: number[] = a.version.split('.').map((p: string) => parseInt(p, 10) || 0);
      const partsB: number[] = b.version.split('.').map((p: string) => parseInt(p, 10) || 0);

      for (let i: number = 0; i < Math.max(partsA.length, partsB.length); i++) {
        const partA: number = partsA[i] || 0;
        const partB: number = partsB[i] || 0;

        if (partA > partB) return -1;
        if (partA < partB) return 1;
      }

      return 0;
    });
  }
}
